import React, { useEffect, useState, useContext, useRef, useCallback } from "react";
import { debounce } from 'lodash';
import { invoiceService } from "../../services/invoice.service";
import CurrencyFormat from "react-currency-format";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import GeneralModal from "../../helpers/GeneralModal";
import { createModal } from "react-modal-promise";
import LogicContext from "../../Context/LogicContext";
import { useForm, Controller } from "react-hook-form";
import { GetInvoiceEdit, ResetInvoiceEdit } from "../../store/invoiceAtom";
import { formatCustomNumber } from "../../helpers/formatCustomNumber";
import { formatFormError } from "../../helpers/formartFormError";
import GeneralCustomerSearch from "../../generals/GeneralCustomerSearch";
import InvoiceWareHouseSelect from "./InvoiceWareHouseSelect";
import InvoiceSalesPersonSelect from "./InvoiceSalesPersonSelect";
import InvoiceCreatedBySelect from "./InvoiceCreatedBy";
import SubscriptionGiftSelect from "../subscription_page/SubscritionGiftSelect";
import GiftBeneficiary from "../subscription_page/GiftBeneficiary";
import AddInvoiceProduct from "./AddInvoiceProduct";
import AddInvoiceSelectitem from "./AddInvoiceSelectItem";
import AddInvoiceTable from "./AddInvoiceTable";
import InvoiceDiscountType from "./InvoiceDiscountType";
import GeneralFileUploadPreview from "../../components/GeneralFileUploadPreview";
import AddOnInvoiceTermsSelect from "./AddInvoiceTermsSelect";

function AddInvoicePage() {
  const invoiceToEdit = GetInvoiceEdit();
  const resetInvoiceToEdit = ResetInvoiceEdit();
  const navigate = useNavigate();
  const myPromiseModal = createModal(GeneralModal);
  const logicContext = useContext(LogicContext);
  const { 
    register, 
    setValue, 
    getValues, 
    handleSubmit, 
    control, 
    reset, 
    watch, 
    formState: { errors } 
  } = useForm({
    defaultValues: {
      Customer_Id: 0,
      productPlanList: [],
      Discount_Amount: 0,
      Shipping_Amount: 0,
      Adjustment_Amount: 0,
      Discount_Type_Id: 1,
      Payment_Term_Id: 0,
      Sales_Person_Id: 0,
      User_Id: 0,
      Gift_Id: 0,
      Warehouse_Id: 0,
      Order_Number: '',
      Payment_Reference: '',
      Payment_Reference_No: '',
      Customer_Notes: '',
      invoice_ref_no: '',
      Due_Date: moment().format("YYYY-MM-DD"),
      Order_Date: moment().format("YYYY-MM-DD"),
      Invoice_Date: moment().format("YYYY-MM-DD"),
    },
  });
  const [selCustomer, setSelCustomer] = useState(null);
  const [attachedFile, setAttachedFile] = useState(null);
  const [productFeature, setProductFeature] = useState(null);
  const [productPlanList, setProductPlanList] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);
  const isMounted = useRef(true);

  // Watch only the fields needed for grandTotal calculation
  const discountAmount = watch("Discount_Amount");
  const shippingAmount = watch("Shipping_Amount");
  const adjustmentAmount = watch("Adjustment_Amount");
  const discountTypeId = watch("Discount_Type_Id");



  // Memoized calculateGrandSum to avoid recalculating unnecessarily
  const calculateGrandSum = useCallback((list, discountAmt, shippingAmt, adjustmentAmt, discountType) => {
    let tempTotal = 0;
    list?.forEach((item) => {
      tempTotal += parseFloat(Number(item?.Quantity) || 0) * parseFloat(Number(item?.Rate) || 0);
    });

    const newDiscountAmt = Number(discountAmt || 0);
    const newAdjustmentAmt = Number(adjustmentAmt || 0);
    const newShippingAmt = Number(shippingAmt || 0);
    const discountTypeId = Number(discountType || 1);

    if (newDiscountAmt + Math.abs(newAdjustmentAmt) > tempTotal) {
      logicContext.showToast('Discount/Adjustment cannot exceed total amount!', "error");
      return 0;
    }

    let gTotal = tempTotal + newShippingAmt + newAdjustmentAmt;
    if (discountTypeId === 1) {
      gTotal -= newDiscountAmt;
    } else if (discountTypeId === 2) {
      gTotal -= (newDiscountAmt / 100) * tempTotal;
    }

    return gTotal || 0;
  }, [logicContext]);

  const calculateSubTotal = useCallback((list) => {
    let tempTotal = 0;
    list?.forEach((item) => {
      tempTotal += parseFloat(item?.Quantity || 0) * parseFloat(item?.Rate || 0);
    });
    return tempTotal || 0;
  }, []);

  // Initialize form with invoiceToEdit data
  useEffect(() => {
    if (!isMounted.current) return;
    if (invoiceToEdit?.Invoice_Ref_No) {
      const fieldsToSet = {
        Customer_Id: invoiceToEdit?.Customer_Id || 0,
        Order_Number: invoiceToEdit?.Order_NO || '',
        Payment_Term_Id: invoiceToEdit?.Payment_Term_Id || 0,
        Sales_Person_Id: invoiceToEdit?.Sales_Person_Id || 0,
        invoice_ref_no: invoiceToEdit?.Invoice_Ref_No || '',
        Warehouse_Id: invoiceToEdit?.Warehouse_Id || 0,
        Payment_Reference: invoiceToEdit?.Payment_Reference || '',
        Payment_Reference_No: invoiceToEdit?.Payment_Reference_No || '',
        Gift_Id: invoiceToEdit?.Gift_Id || 0,
        Discount_Amount: invoiceToEdit?.Discount_Amount || 0,
        Shipping_Amount: invoiceToEdit?.Shipping_Amount || 0,
        Adjustment_Amount: invoiceToEdit?.Adjustment_Amount || 0,
        Customer_Notes: invoiceToEdit?.Customer_Notes || '',
        Discount_Type_Id: invoiceToEdit?.Discount_Type_Id || 1,
        Due_Date: invoiceToEdit?.Due_Date_String
          ? moment(invoiceToEdit.Due_Date_String, "DD-MM-YYYY").format("YYYY-MM-DD")
          : moment().format("YYYY-MM-DD"),
        Order_Date: invoiceToEdit?.Order_Date_String
          ? moment(invoiceToEdit.Order_Date_String, "DD-MM-YYYY").format("YYYY-MM-DD")
          : moment().format("YYYY-MM-DD"),
        Invoice_Date: invoiceToEdit?.Invoice_Date_String
          ? moment(invoiceToEdit.Invoice_Date_String, "DD-MM-YYYY").format("YYYY-MM-DD")
          : moment().format("YYYY-MM-DD"),
      };

      const newProductPlanList = invoiceToEdit["invoice details"]?.map((item) => ({
        ...item,
        label: item.Item_Name,
        Rate: item.Item_Sales_Rate || 0,
        Quantity: parseFloat(item.Item_Sales_Quantity) || 0,
        tax_id: parseInt(item.Item_Tax_Id) || 0,
      })) || [];

      // Only update productPlanList if it's different to avoid infinite loop
      if (JSON.stringify(productPlanList) !== JSON.stringify(newProductPlanList)) {
        setProductPlanList(newProductPlanList);
        setValue("productPlanList", newProductPlanList, { shouldValidate: false });
      }

      // Only set form values if they differ
      Object.entries(fieldsToSet).forEach(([key, value]) => {
        if (getValues(key) !== value) {
          setValue(key, value, { shouldValidate: false });
        }
      });

      setSelCustomer({
        Name: invoiceToEdit?.Customer_Full_Name,
        Customer_Id: invoiceToEdit?.Customer_Id,
      });
    }

    return () => {
      // resetInvoiceToEdit();
      isMounted.current = false;
    };
  }, [invoiceToEdit, setValue, getValues, productPlanList]);

  // Recalculate grandTotal when specific fields change
  useEffect(() => {
    const newTotal = calculateGrandSum(
      productPlanList,
      discountAmount,
      shippingAmount,
      adjustmentAmount,
      discountTypeId
    );
    setGrandTotal(newTotal);
  }, [productPlanList, discountAmount, shippingAmount, adjustmentAmount, discountTypeId, calculateGrandSum]);

  const debouncedOnCustomerSelect = useCallback(
    debounce((customer) => {
      setSelCustomer(customer);
      setValue("Customer_Id", customer.Customer_Id, { shouldValidate: true });
    }, 300),
    [setValue]
  );

  const onCustomerSelect = useCallback((customer) => {
    debouncedOnCustomerSelect(customer);
  }, [debouncedOnCustomerSelect]);

  const onProductSelect = useCallback((feature) => {
    setProductFeature(feature);
  }, []);

  const onItemSelect = useCallback((selItem) => {
    const firstItem = productPlanList[0];
    if (firstItem?.Product_Feature_Id && firstItem?.Product_Feature_Id !== selItem?.Product_Feature_Id) {
      return logicContext.showToast('Multiple product types are not allowed!', "error");
    }

    const alreadyExistItem = productPlanList.find(item => item.Item_Id === selItem?.Item_Id);
    if (alreadyExistItem) {
      return logicContext.showToast('This item is already selected!', "error");
    }

    const newProductPlanList = [
      ...productPlanList,
      {
        ...selItem,
        label: selItem.Item_Name,
        Rate: selItem?.Item_Sales_Rate || selItem?.Rate || 0,
        Quantity: 1,
        tax_id: 0,
      },
    ];
    setProductPlanList(newProductPlanList);
    setValue("productPlanList", newProductPlanList, { shouldValidate: true });
  }, [productPlanList, logicContext, setValue]);

  const onPaymentTermsSelect = useCallback((val) => {
    setValue("Payment_Term_Id", val.Payment_Term_Id, { shouldValidate: true });
  }, [setValue]);

  const onSalesPersonSelect = useCallback((val) => {
    setValue("Sales_Person_Id", val.User_Id, { shouldValidate: true });
  }, [setValue]);

  const onSalesCreatedBySelect = useCallback((val) => {
    setValue("User_Id", val.User_Id, { shouldValidate: true });
  }, [setValue]);

  const onGiftChecked = useCallback((code) => {
    setValue("Gift_Id", code, { shouldValidate: true });
  }, [setValue]);

  const onGiftPersonSelect = useCallback((giftPerson) => {
    setValue("gift_recipient_ref_no", giftPerson?.Recipient_Ref_No, { shouldValidate: true });
  }, [setValue]);

  const onWareHouseSelect = useCallback((val) => {
    setValue("Warehouse_Id", val.Warehouse_Id, { shouldValidate: true });
  }, [setValue]);

  const onDiscountTypeSelect = useCallback((val) => {
    setValue("Discount_Type_Id", val, { shouldValidate: true });
  }, [setValue]);


  const onQuantityChange = useCallback((value, index) => {
    const newQuantity = parseFloat(value) || 0;
    if (newQuantity >= 0) {
      const newProductPlanList = productPlanList.map((item, i) =>
        i === index ? { ...item, Quantity: newQuantity } : item
      );
      setProductPlanList(newProductPlanList);
      setValue("productPlanList", newProductPlanList, { shouldValidate: true });
    }
  }, [productPlanList, setValue]);




  const removeOldAddon = useCallback((index) => {
    const newProductPlanList = productPlanList.filter((_, i) => i !== index);
    setProductPlanList(newProductPlanList);
    setValue("productPlanList", newProductPlanList, { shouldValidate: true });
  }, [productPlanList, setValue]);



  const onSubmit = useCallback(async (data) => {
    const tempStringList = data.productPlanList?.map((item) => `
      <a>
        <item_id>${item.Item_Id}</item_id>
        <rate>${item?.Rate || 0}</rate>
        <quantity>${item?.Quantity || 0}</quantity>
        <tax_id>${item?.tax_id || 0}</tax_id>
      </a>
    `).join('');

    try {
      const res = await myPromiseModal({
        title: "Confirmation",
        body: `${invoiceToEdit?.Invoice_Ref_No ? "Update invoice?" : "Save this invoice?"}`,
        showInput: false,
      });

      if (res.decision === true) {
        let option = 1;
        sessionStorage.setItem('reloadPage', 'true');
        if (invoiceToEdit?.Invoice_Ref_No) {
          option = 3;
          sessionStorage.removeItem('reloadPage');
        }

        const formData = new FormData();
        for (const keyData in data) {
          if (keyData !== 'productPlanList') {
            formData.append(keyData, data[keyData] ?? '');
          }
        }
        formData.append('option', option);
        formData.append('xml_string_item', `<root>${tempStringList}</root>`);
        if (attachedFile) {
          formData.append('file', attachedFile);
        }

        const response = await invoiceService.submitInvoice(formData);
        if (response?.data) {
          const cleanResponse =  logicContext.httpResponseManager(response.data?.[0] || response.data?.result_status?.[0]);
          logicContext.showToast(cleanResponse?.message_text, 'success');
            navigate(-1);
          } 
        }
      
    } catch (error) {
      if (isMounted.current) {
        // logicContext.showToast("Error submitting invoice", "error");
      }
    }
  }, [attachedFile, invoiceToEdit, logicContext, myPromiseModal, navigate]);



  const onErrors = useCallback((errors) => {
    const errorMessages = formatFormError(errors);
    logicContext.showToast(errorMessages, "error");
  }, [logicContext]);



  const onFileSelected = useCallback((file) => {
    setAttachedFile(file);
  }, []);



  const dueDateFunction = useCallback(() => {
    let defaultDate = moment().format("YYYY-MM-DD");
    if (invoiceToEdit?.Due_Date_String) {
      defaultDate = moment(invoiceToEdit.Due_Date_String, "DD-MM-YYYY").format("YYYY-MM-DD");
    }

    return (
      <input
        defaultValue={defaultDate}
        className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-100"
        type="date"
        {...register("Due_Date", { required: "Due date is required" })}
      />
    );
  }, [invoiceToEdit, register]);

  const orderDateFunction = useCallback(() => {
    let defaultDate = moment().format("YYYY-MM-DD");
    if (invoiceToEdit?.Order_Date_String) {
      defaultDate = moment(invoiceToEdit.Order_Date_String, "DD-MM-YYYY").format("YYYY-MM-DD");
    }

    return (
      <input
        defaultValue={defaultDate}
        className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-100"
        type="date"
        {...register("Order_Date", { required: "Order date is required" })}
      />
    );
  }, [invoiceToEdit, register]);

  const invoiceDateFunction = useCallback(() => {
    let defaultDate = moment().format("YYYY-MM-DD");
    if (invoiceToEdit?.Invoice_Date_String) {
      defaultDate = moment(invoiceToEdit.Invoice_Date_String, "DD-MM-YYYY").format("YYYY-MM-DD");
    }

    return (
      <input
        defaultValue={defaultDate}
        className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-100"
        type="date"
        {...register("Invoice_Date", { required: "Invoice date is required" })}
      />
    );
  }, [invoiceToEdit, register]);

  return (
    <form onSubmit={handleSubmit(onSubmit, onErrors)} className="container mx-auto p-6 bg-white shadow-lg rounded-lg max-w-6xl">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">{invoiceToEdit?.Invoice_Ref_No ? 'Edit Invoice' : 'Add Invoice'}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Customer Name */}
        <div>
          <label className="text-sm font-medium text-red-600 mb-1">Customer Name</label>
          <Controller
            name="Customer_Id"
            control={control}
            rules={{ 
              required: "Customer selection is required",
              validate: (value) => value !== 0 || "Please select a valid customer"
            }}
            render={({ field }) => (
              <GeneralCustomerSearch 
                defaultVal={invoiceToEdit?.Customer_Full_Name} 
                onCustomerSelect={onCustomerSelect}
              />
            )}
          />
          {errors.Customer_Id && (
            <p className="mt-1 text-sm text-red-600">{errors.Customer_Id.message}</p>
          )}
          {selCustomer && (
            <div className="mt-3 p-3 border border-gray-300 rounded-md bg-gray-50">
              <p className="font-semibold">Customer: {selCustomer?.Name}</p>
              <p className="font-semibold">ID: {selCustomer?.Customer_Id}</p>
            </div>
          )}
        </div>

        {/* Warehouse Name */}
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1">Warehouse Name (<small>select</small>)</label>
          <InvoiceWareHouseSelect
            onWareHouseSelect={onWareHouseSelect}
            defaultVal={{ label: invoiceToEdit?.Warehouse_Name }}
          />
        </div>

        {/* Invoice Number */}
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1">Invoice Number</label>
          <input
            className="w-full p-2.5 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
            readOnly
            placeholder="Auto Generated Number"
            value={invoiceToEdit?.Invoice_Ref_No || ''}
          />
        </div>

        {/* Invoice Date */}
        <div>
          <label className="text-sm font-medium text-red-600 mb-1">Invoice Date</label>
          {invoiceDateFunction()}
          {errors.Invoice_Date && (
            <p className="mt-1 text-sm text-red-600">{errors.Invoice_Date.message}</p>
          )}
        </div>

        {/* Order Number */}
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1">Order Number (<small>auto generated</small>)</label>
          <input
            className="w-full p-2.5 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
            type="text"
            readOnly
            {...register("Order_Number")}
          />
        </div>

        {/* Order Date */}
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1">Order Date</label>
          {orderDateFunction()}
          {errors.Order_Date && (
            <p className="mt-1 text-sm text-red-600">{errors.Order_Date.message}</p>
          )}
        </div>

        {/* Terms */}
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1">Terms</label>
          <AddOnInvoiceTermsSelect
            onPaymentTermsSelect={onPaymentTermsSelect}
            defaultVal={{ label: invoiceToEdit?.Payment_Term_Name }}
          />
        </div>

        {/* Due Date */}
        <div>
          <label className="text-sm font-medium text-red-600 mb-1">Due Date</label>
          {dueDateFunction()}
          {errors.Due_Date && (
            <p className="mt-1 text-sm text-red-600">{errors.Due_Date.message}</p>
          )}
        </div>

        {/* Sales Person */}
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1">Sales Person</label>
          <InvoiceSalesPersonSelect
            onSalesPersonSelect={onSalesPersonSelect}
            defaultVal={{ label: invoiceToEdit?.Sales_Person_Name }}
          />
        </div>

        {/* Created By */}
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1">Created By</label>
          <InvoiceCreatedBySelect
            onSalesCreatedBySelect={onSalesCreatedBySelect}
            defaultVal={{ label: invoiceToEdit?.Created_User_Full_Name }}
          />
        </div>

        {/* Is this a gift? */}
        <div className="md:col-span-2">
          <label className="text-sm font-medium text-gray-700">Is this a gift? <span className="text-red-500">(Select YES if applicable)</span></label>
          <SubscriptionGiftSelect val={getValues("Gift_Id") || 0} onGiftChecked={onGiftChecked} />
          {getValues("Gift_Id") === 1 && (
            <GiftBeneficiary
              isAGift={getValues("Gift_Id")}
              Customer_Id={getValues("Customer_Id")}
              onGiftPersonSelect={onGiftPersonSelect}
            />
          )}
        </div>

        {/* Subscription Ref. No. */}
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1">Subscription Ref. No. (<small>auto generated</small>)</label>
          <input
            className="w-full p-2.5 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
            type="text"
            readOnly
            value={invoiceToEdit?.Subscription_Ref_No || ''}
          />
        </div>

        {/* Payment Reference */}
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1">Payment Reference</label>
          <input
            className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-100"
            type="text"
            {...register("Payment_Reference")}
          />
        </div>

        {/* Payment Reference Number */}
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1">Payment Reference Number</label>
          <input
            className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-100"
            type="text"
            {...register("Payment_Reference_No")}
          />
        </div>

        {/* Product & Item Selection */}
        <div className="md:col-span-2 mt-4 p-6 border border-gray-300 rounded-lg bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-bold text-gray-700 mb-1">Product <span className="text-red-500">(Select product)</span></label>
              <AddInvoiceProduct onProductSelect={onProductSelect} />
            </div>
            <div>
              <label className="text-sm font-bold text-gray-700 mb-1">Item <span className="text-red-500">(Select product first)</span></label>
              <Controller
                name="productPlanList"
                control={control}
                rules={{ 
                  validate: (value) => value?.length > 0 || "At least one product must be selected"
                }}
                render={({ field }) => (
                  <AddInvoiceSelectitem onItemSelect={onItemSelect} productFeature={productFeature} />
                )}
              />
              {errors.productPlanList && (
                <p className="mt-1 text-sm text-red-600">{errors.productPlanList.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* Invoice Table */}
        <div className="md:col-span-2">
          <AddInvoiceTable
            productPlanList={productPlanList}
            removeOldAddon={removeOldAddon}
            onQuantityChange={onQuantityChange}
          />
        </div>

        {/* Summary Section */}
        <div className="md:col-span-2 p-6 border border-gray-300 rounded-lg bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="font-medium text-blue-600">Sub Total</div>
            <div></div>
            <div className="text-right text-blue-600">
              <CurrencyFormat value={formatCustomNumber(calculateSubTotal(productPlanList))} displayType={"text"} thousandSeparator={true} prefix={"₦"} />
            </div>

            <div className="font-medium text-blue-600 text-sm">Discount</div>
            <div className="relative">
              <input
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-100"
                type="number"
                step="any"
                {...register("Discount_Amount", { 
                  valueAsNumber: true, 
                  min: { value: 0, message: "Discount cannot be negative" } 
                })}
              />
              <div className="absolute top-0 right-0 mt-2 mr-2">
                <InvoiceDiscountType 
                  onDiscountTypeSelect={onDiscountTypeSelect} 
                  defaultVal={invoiceToEdit?.Discount_Type_Id}
                />
              </div>
            </div>
            <div className="text-right text-sm">
              <CurrencyFormat value={formatCustomNumber(getValues("Discount_Amount") || 0)} displayType={"text"} thousandSeparator={true} prefix={""} />
            </div>

            <div className="text-sm text-gray-600">Shipping Charges <br /><i className="text-blue-600">Apply Tax on shipping</i></div>
            <input
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-100"
              type="number"
              step="any"
              {...register("Shipping_Amount", { 
                valueAsNumber: true, 
                min: { value: 0, message: "Shipping cannot be negative" } 
              })}
            />
            <div className="text-right text-blue-600">
              <CurrencyFormat value={formatCustomNumber(getValues("Shipping_Amount") || 0)} displayType={"text"} thousandSeparator={true} prefix={"₦"} />
            </div>

            <div className="text-sm text-gray-600">Adjustment</div>
            <input
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-100"
              type="number"
              step="any"
              {...register("Adjustment_Amount", { valueAsNumber: true })}
            />
            <div className="text-right">
              <span className={getValues("Adjustment_Amount") < 0 ? 'text-red-600' : 'text-green-600'}>
                <CurrencyFormat value={formatCustomNumber(getValues("Adjustment_Amount") || 0)} displayType={"text"} thousandSeparator={true} prefix={"₦"} />
              </span>
            </div>

            <div className="text-xl font-bold">Total (₦)</div>
            <div></div>
            <div className="text-right text-xl font-bold">
              <CurrencyFormat value={formatCustomNumber(grandTotal)} displayType={"text"} thousandSeparator={true} prefix={"₦"} />
            </div>
          </div>
        </div>

        {/* Customer Notes & Terms and Conditions */}
        <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1">Customer Notes</label>
            <textarea
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-100 h-32"
              {...register("Customer_Notes")}
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1">Terms and Conditions</label>
            <textarea
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-100 h-32"
              readOnly
              placeholder="Enter the terms and conditions of your business to be displayed in your transaction"
            />
          </div>
        </div>

        {/* Attach File */}
        <div className="md:col-span-2">
          <label className="text-sm font-medium text-gray-700 mb-1">Attach File</label>
          <GeneralFileUploadPreview
            defaultFileUrl={invoiceToEdit?.Attachement_URL}
            onFileSelected={onFileSelected}
          />
          <p className="text-sm text-gray-500 mt-1">Maximum file size: 2MB</p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mt-8 flex justify-end space-x-4">
        <button
          type="button"
          className="px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-600 rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500"
          onClick={() => navigate(-1)}
        >
          CANCEL
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-600 rounded-md hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          SAVE AS DRAFT
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          SAVE
        </button>
      </div>
    </form>
  );
}

export default AddInvoicePage;